import { useDispatch, useSelector } from 'react-redux';
import { captureMessage as captureMessageImport } from '@tink/core/src/sentry';
import { useEffect, useMemo, useState } from 'react';
import { accountsService as accountsServiceImport } from '../../../../services/accounts';
import { isAccountCheckBundled } from '../../../../models/configuration';
import { useConfiguration } from '../../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { AppState } from '../../../../stores/redux-store';
import { PayPayload } from '../../../../models/ApplicationSuccessResult';
import { applicationParameters } from '../../../../utilities/application-parameters';
import { accountCheckServiceRedux } from '../../../../banking/account-check/services/account-check';
import { Transfer } from '../../../DirectPayments/models/transfer';
import { fetchPaymentTransferDetails as fetchPaymentTransferDetailsImport } from '../../services/payments-services';

export const useApplicationSuccessResultPayloadPay = (
  accountsService = accountsServiceImport,
  fetchPaymentTransferDetails = fetchPaymentTransferDetailsImport,
  accountCheckService = accountCheckServiceRedux,
  captureMessage = captureMessageImport
) => {
  const { data: config } = useConfiguration();
  const [pendingAC, setPendingAC] = useState(isAccountCheckBundled(config));
  const dispatch = useDispatch();
  const paymentRequestId = applicationParameters.get('paymentRequestId') as string;
  const transfer = useSelector((state: AppState) => state.transfer.data);
  const paymentTransferDetails = useSelector(
    (state: AppState) => state.paymentTransferDetails.data
  );
  const accounts = useSelector((state: AppState) => state.accounts.data.accounts);
  const accountVerificationReportId = useSelector((state: AppState) => state.accountCheck.data.id);
  const isEnabled = pendingAC && Transfer.isSent(transfer) && !accountVerificationReportId;

  useEffect(() => {
    if (accountVerificationReportId) {
      setPendingAC(false);
    }
  }, [accountVerificationReportId]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (!accounts) {
      dispatch(accountsService.fetchAccounts(paymentRequestId));
    }

    // For PIS only we want be able to fetch account and we will only receive empty array back
    if (accounts && accounts.length === 0) {
      setPendingAC(false);
    }
  }, [accounts, accountsService, dispatch, isEnabled, paymentRequestId]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (transfer && !paymentTransferDetails) {
      dispatch(fetchPaymentTransferDetails(transfer.id));
    }
  }, [dispatch, fetchPaymentTransferDetails, isEnabled, paymentTransferDetails, transfer]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (paymentTransferDetails && accounts && accounts.length > 0 && !accountVerificationReportId) {
      const { source } = paymentTransferDetails;
      const matchedAccount =
        source && accounts.find((account) => account.identifiers.includes(source.uri));

      if (matchedAccount) {
        dispatch(accountCheckService.createReport([matchedAccount.id]));
      } else {
        captureMessage('paymentTransferDetails could not match account', {
          tags: {
            PaymentRequest: paymentRequestId,
            paymentTransferDetails: JSON.stringify(paymentTransferDetails),
          },
        });
        setPendingAC(false);
      }
    }
  }, [
    accountCheckService,
    accountVerificationReportId,
    accounts,
    captureMessage,
    dispatch,
    isEnabled,
    paymentRequestId,
    paymentTransferDetails,
  ]);

  const successResult: PayPayload = useMemo(() => {
    if (isAccountCheckBundled(config) && accountVerificationReportId) {
      return { paymentRequestId, accountVerificationReportId };
    }

    return { paymentRequestId };
  }, [accountVerificationReportId, config, paymentRequestId]);

  return {
    pendingAC,
    successResult,
  };
};
